import React from 'react';
import { Button, Input, Tag } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';

const { TextArea } = Input;

interface EditableFieldProps {
  fieldKey: string;
  value: string;
  isTextArea?: boolean;
  isEditing: boolean;
  tempValue: string;
  onEdit: (field: string, value: string) => void;
  onSave: (field: string) => void;
  onCancel: () => void;
  onTempValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean; // 无编辑权限时只读显示
}

interface EditableTagsProps {
  tags: string[];
  isEditing: boolean;
  tempTagValue: string;
  onEdit: () => void;
  onAddTag: () => void;
  onRemoveTag: (tag: string) => void;
  onCancel: () => void;
  onTempTagValueChange: (value: string) => void;
  disabled?: boolean;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  fieldKey,
  value,
  isTextArea = false,
  isEditing,
  tempValue,
  onEdit,
  onSave,
  onCancel,
  onTempValueChange,
  placeholder,
  disabled = false
}) => {
  if (isEditing) {
    return (
      <div className="flex items-center space-x-2 flex-1">
        {isTextArea ? (
          <TextArea
            value={tempValue}
            onChange={(e) => onTempValueChange(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                onSave(fieldKey);
              }
            }}
            autoSize={{ minRows: 1, maxRows: 3 }}
            className="flex-1"
          />
        ) : (
          <Input
            value={tempValue}
            onChange={(e) => onTempValueChange(e.target.value)}
            onPressEnter={() => onSave(fieldKey)}
            className="flex-1"
          />
        )}
        <Button 
          type="text" 
          size="small" 
          icon={<CheckOutlined />}
          onClick={() => onSave(fieldKey)}
          className="text-gray-500 hover:text-green-500 transition-colors"
          style={{ color: '#666' }}
        />
        <Button 
          type="text"
          size="small" 
          icon={<CloseOutlined />}
          onClick={onCancel}
          className="text-gray-500 hover:text-red-500 transition-colors"
          style={{ color: '#666' }}
        />
      </div>
    );
  }
  
  // 无编辑权限时只显示纯文本
  if (disabled) {
    return (
      <div className="flex items-center space-x-2 flex-1">
        <span className="flex-1 text-gray-600 text-sm px-2 py-1">
          {value || '-'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 flex-1 group">
      <span 
        className="flex-1 text-gray-600 text-sm cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors" 
        onClick={() => onEdit(fieldKey, value)}
        title="点击编辑"
      >
        {value || <span className="text-gray-400 italic">{placeholder || '点击添加内容'}</span>}
      </span>
      <Button 
        type="link" 
        size="small" 
        icon={<EditOutlined />} 
        className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500"
        onClick={() => onEdit(fieldKey, value)}
      />
    </div>
  );
};

export const EditableTags: React.FC<EditableTagsProps> = ({
  tags,
  isEditing,
  tempTagValue,
  onEdit,
  onAddTag,
  onRemoveTag,
  onCancel,
  onTempTagValueChange,
  disabled = false,
}) => {
  if (isEditing) {
    return (
      <div className="flex items-center space-x-2 flex-1">
        <div className="flex flex-wrap gap-1 flex-1">
          {tags.map((tag: string, index: number) => (
            <Tag 
              key={index} 
              color="green" 
              closable 
              onClose={() => onRemoveTag(tag)}
            >
              {tag}
            </Tag>
          ))}
          <Input
            size="small"
            value={tempTagValue}
            onChange={(e) => onTempTagValueChange(e.target.value)}
            onPressEnter={onAddTag}
            placeholder="输入标签后按回车"
            style={{ width: '120px' }}
          />
        </div>
        <Button 
          type="text" 
          size="small" 
          icon={<CheckOutlined />}
          onClick={onCancel}
          className="text-gray-500 hover:text-green-500 transition-colors"
          style={{ color: '#666' }}
        />
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="flex items-center space-x-2 flex-1 px-2 py-1">
        <div className="flex flex-wrap gap-1 flex-1">
          {tags.map((tag: string, index: number) => (
            <Tag key={index} color="green">{tag}</Tag>
          ))}
          {(!tags || tags.length === 0) && (
            <span className="text-gray-400 text-sm italic">-</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 flex-1 group">
      <div 
        className="flex flex-wrap gap-1 flex-1 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
        onClick={onEdit}
        title="点击编辑标签"
      >
        {tags.map((tag: string, index: number) => (
          <Tag key={index} color="green">{tag}</Tag>
        ))}
        {(!tags || tags.length === 0) && (
          <span className="text-gray-400 text-sm italic">点击添加标签</span>
        )}
      </div>
      <Button 
        type="link" 
        size="small" 
        icon={<EditOutlined />} 
        className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500"
        onClick={onEdit}
      />
    </div>
  );
};